import React, { CSSProperties, ReactElement } from 'react';
import { reactNodeIsOfType } from '../../../utils';
import { VerticalPanel } from '../verticalpanel';
import { Panel } from '../panel';

/**
 * {@docCategory CardPanel}
 */
export interface CardPanelProps {
   /**
    * The id of the active child.
    */
   activeCardId?: string;

   /**
    * Determines if the panel has a border around it.
    *
    * @defaultvalue false
    */
   border?: boolean;

   /**
    * Sets a className on the panel.
    */
   className?: string;

   /**
    * Sets styles on the panel.
    */
   style?: CSSProperties;
}

/**
 * {@docCategory CardPanel}
 */
export interface CardPanelItemProps {
   /**
    * The id of the card.
    */
   id: string;

   /**
    * Set to true to unmount the card content from the DOM when inactive.
    */
   destroyWhenInactive?: boolean;

   /**
    * Set to true to render the card content on initial render.
    */
   forceRender?: boolean;

   /**
    * Sets a className on the panel.
    */
   className?: string;

   /**
    * Sets styles on the panel.
    */
   style?: CSSProperties;
}

export const CardPanel = (props: React.PropsWithChildren<CardPanelProps>) => {
   return (
      <VerticalPanel
         border={props.border}
         className={`uf-card-panel ${props.className || ''}`}
         style={props.style}
      >
         {React.Children.map(props.children, (child) => {
            const childElement = child as ReactElement;
            const childProps = childElement.props as CardPanelItemProps;

            if (reactNodeIsOfType(child, CardPanelItem)) {
               const childElement = child as ReactElement;
               const active = childElement.props.id === props.activeCardId;

               return <CardPanelItemWrapper
                  {...childElement.props}
                  id={childProps.id}
                  active={active}
               />;
            }
         })}
      </VerticalPanel>
   );
};

export class CardPanelItem extends React.Component<React.PropsWithChildren<CardPanelItemProps>> {
}

interface CardPanelItemWrapperProps extends CardPanelItemProps {
   active: boolean;
}

const CardPanelItemWrapper = (props: React.PropsWithChildren<CardPanelItemWrapperProps>) => {
   const active = props.active;
   const forceRender = props.forceRender === undefined ? false : props.forceRender;
   const destroyWhenInactive = props.destroyWhenInactive === undefined ? false : props.destroyWhenInactive;

   const [visited, setVisited] = React.useState(forceRender);

   React.useEffect(() => {
      if (active) {
         setVisited(true);
      }
      else if (destroyWhenInactive) {
         setVisited(false);
      }
   }, [active, destroyWhenInactive]);

   return (
      <Panel
         key={props.id}
         className={`uf-card-panel-item ${props.active ? 'uf-card-panel-item-active' : 'uf-card-panel-item-inactive'}`}
         fill={true}
      >
         {(active || visited || forceRender) && props.children}
      </Panel>
   );
};
